import React, { useEffect, useState } from 'react';
import './RoleRotator.css';

/**
 * RoleRotator — the hero accent line that cycles through concrete roles
 * ("for cold outreach." … "as your chief of staff."), making the "any role"
 * promise tangible (the Manus-style role swap). Used by AIEHero and HomeFold,
 * so it appears on BOTH "/" and "/ai-employees".
 *
 * TRANSLATION MODEL (Sep 27 2026, Nitish: "everything else gets translated but
 * 'as your chief of staff' doesn't"): the phrases are rendered ONCE, stacked in
 * the same spot, and rotation only swaps CSS classes. Text nodes are never
 * replaced, so a page translator (Chrome Translate, Google Translate) converts
 * all phrases once and they keep rotating translated. The old model remounted
 * a fresh English span every 2.2s, which the translator did not reliably catch.
 * Do NOT reintroduce key={phrase} remounts or text swaps here.
 *
 * Motion model (vertical ticker, unchanged): on every swap the outgoing phrase
 * slides UP and fades while the incoming phrase rises in from below.
 *
 * SEO / a11y / CLS invariants (per the 2026 rotating-hero research):
 *  - PHRASES[0] MUST stay "for any role." — it's the first render, which is
 *    what react-snap prerenders and JS-blind crawlers read. The other phrases
 *    are added on the client after mount, and NEVER for react-snap or known
 *    crawler user agents (STATIC_AGENT), so the H1 text crawlers index stays
 *    "Hire an AI employee for any role."
 *  - The parent H1 carries a stable aria-label; this whole rotator is inside
 *    an aria-hidden span, so screen readers get one static sentence.
 *  - prefers-reduced-motion → one phrase only, no interval, no animation.
 *  - Single-line, nowrap: the active phrase is the only one in flow; hidden
 *    phrases are absolutely positioned (zero footprint), so no layout shift
 *    beyond today's per-phrase width change.
 */
export const ROLE_PHRASES = [
  'for any role.',
  'for cold outreach.',
  'for deep research.',
  'as your CMO.',
  'as your chief of staff.',
  'for account management.',
  'for customer support.',
  'as your personal assistant.',
];

const ROTATE_MS = 2200;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// react-snap crawls with a "ReactSnap" user agent; search and AI crawlers are
// listed by name (never a bare "bot": real Android phones include "CUBOT").
const STATIC_AGENT = /ReactSnap|Googlebot|Google-InspectionTool|bingbot|DuckDuckBot|YandexBot|Baiduspider|Applebot|GPTBot|ClaudeBot|PerplexityBot|facebookexternalhit|Twitterbot|LinkedInBot/i;
const isStaticAgent = () =>
  typeof navigator !== 'undefined' && STATIC_AGENT.test(navigator.userAgent);

const RoleRotator = () => {
  // full = all phrases mounted (client only, after first paint matches the prerender).
  // tick counts swaps; the active and outgoing indices derive from it.
  const [full, setFull] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (prefersReducedMotion() || isStaticAgent()) return undefined;
    setFull(true);
    const id = setInterval(() => setTick((t) => t + 1), ROTATE_MS);
    return () => clearInterval(id);
  }, []);

  const n = ROLE_PHRASES.length;
  const index = tick % n;
  const prevIndex = (tick + n - 1) % n;
  const phrases = full ? ROLE_PHRASES : ROLE_PHRASES.slice(0, 1);

  return (
    <span className="aie-role-rotator">
      {phrases.map((phrase, i) => {
        let state = 'is-idle';
        if (i === index) state = 'is-active';
        else if (tick > 0 && i === prevIndex) state = 'is-out';
        return (
          /* Stable key = position: the node and its text are never replaced, only its class. */
          // eslint-disable-next-line react/no-array-index-key
          <span key={i} className={`aie-role-rotator__phrase aie-hero__title-accent ${state}`}>
            {phrase}
          </span>
        );
      })}
    </span>
  );
};

export default RoleRotator;
